import React from 'react';

import { CoverageSchema } from '@/services/api/generated/matterChangesService';

import { ActivityDescription } from './ActivityDescription';
import { formatCoverageValue, formatEnumValue, formatFieldName } from './utils';

export interface CoverageFieldsProps {
  coverage: CoverageSchema | null | undefined;
}

/**
 * Renders all non-null fields from a coverage object
 */
export const CoverageFields: React.FC<CoverageFieldsProps> = ({ coverage }) => {
  if (!coverage) return null;

  // Define which fields to display and in what order
  const fieldsToDisplay: (keyof CoverageSchema)[] = [
    'policy_type',
    'carrier',
    'policy_identifiers',
    'category',
    'named_insured',
    'benefits',
    'adjusters',
    'claims',
    'vehicles',
    'policy_details',
    'subrogator',
    'status',
    'liability_status',
  ];

  const fields = fieldsToDisplay
    .map((fieldName) => {
      const value = coverage[fieldName];

      // Skip null/undefined values
      if (value === null || value === undefined) return null;

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) return null;

      let displayValue: string | null = null;

      // Special handling for specific fields
      if (
        fieldName === 'carrier' &&
        typeof value === 'object' &&
        value !== null
      ) {
        const carrier = value as { name?: string };
        displayValue = carrier.name || null;
      } else if (fieldName === 'policy_identifiers' && Array.isArray(value)) {
        displayValue = value
          .map((item) => {
            const identifier = item as { identifier_value?: string };
            return identifier.identifier_value;
          })
          .filter(Boolean)
          .join(', ');
      } else if (
        fieldName === 'policy_type' ||
        fieldName === 'category' ||
        fieldName === 'status' ||
        fieldName === 'liability_status'
      ) {
        displayValue = formatEnumValue(value as string);
      } else {
        displayValue = formatCoverageValue(value);
      }

      if (!displayValue) return null;

      return {
        name: fieldName,
        label: formatFieldName(fieldName),
        value: displayValue,
      };
    })
    .filter((field): field is NonNullable<typeof field> => field !== null);

  if (fields.length === 0) return null;

  return (
    <div className="space-y-1">
      {fields.map((field) => (
        <ActivityDescription key={field.name}>
          <span className="font-medium">{field.label}:</span> {field.value}
        </ActivityDescription>
      ))}
    </div>
  );
};
